import React, { useEffect, useRef, useState } from "react";
import rrwebPlayer from "rrweb-player";
import "rrweb-player/dist/style.css";
import "./RRWebPlayer.scss";
import { PulseSpinnerIcon } from "@pulselabs-ai/shared-fe2";

// Define more specific types for rrweb events
interface RRWebEvent {
  type: number;
  data: unknown;
  timestamp: number;
  delay?: number;
}

interface RRWebPlayerProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  events: RRWebEvent[] | string; // Can be array of events or URL string
  width?: number;
  height?: number;
  maxScale?: number;
  autoPlay?: boolean;
  speed?: number;
  speedOption?: number[];
  showController?: boolean;
  tags?: Record<string, unknown>;
  inactiveColor?: string;
  className?: string;
  containerClassName?: string;
  // Optional callback to access the player instance
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onPlayerReady?: (player: any) => void;
}

const RRWebPlayer: React.FC<RRWebPlayerProps> = ({
  events,
  width = 1024,
  height = 576,
  maxScale = 1,
  autoPlay = true,
  speed = 1,
  speedOption = [1, 2, 4, 8],
  showController = true,
  tags = {},
  inactiveColor = "#D4D4D4",
  className = "",
  containerClassName = "",
  onPlayerReady,
  ...restProps
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const playerInstanceRef = useRef<any>(null);
  const [eventData, setEventData] = useState<RRWebEvent[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchEvents = async (url: string) => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await fetch(url);
        // , {
        //   headers: {
        //     Accept: "application/json",
        //     "Content-Type": "application/json",
        //   },
        //   credentials: "include", // Include credentials if needed
        // }
        if (!response.ok) {
          throw new Error(`Failed to fetch events: ${response.statusText}`);
        }
        const data = await response.json();
        setEventData(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Error fetching events:", err);
        setError(err instanceof Error ? err.message : "Failed to load events");
      } finally {
        setIsLoading(false);
      }
    };

    // Check if events is a URL string or an array
    if (typeof events === "string" && events.trim() !== "") {
      fetchEvents(events);
    } else if (Array.isArray(events)) {
      setEventData(events);
    }
  }, [events]);

  useEffect(() => {
    if (!containerRef.current || !eventData.length) return;

    // Capture the container ref's current value
    const containerElement = containerRef.current;

    // Initialize the rrweb player
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const player = new rrwebPlayer({
      target: containerElement, // Use the captured value
      props: {
        events: eventData,
        width,
        height,
        maxScale,
        autoPlay,
        speed,
        speedOption,
        showController,
        tags,
        inactiveColor,
        ...restProps,
      },
    });

    // Store the player instance for later use
    playerInstanceRef.current = player;

    // Notify parent component if needed
    if (onPlayerReady) {
      onPlayerReady(player);
    }

    // Clean up on unmount
    return () => {
      if (playerInstanceRef.current) {
        try {
          // Try to remove all content from the container using the captured value
          if (containerElement) {
            containerElement.innerHTML = "";
          }
          // Clear the reference
          playerInstanceRef.current = null;
        } catch (err) {
          console.error("Error cleaning up rrweb player:", err);
        }
      }
    };
  }, [
    eventData,
    width,
    height,
    maxScale,
    autoPlay,
    speed,
    speedOption,
    showController,
    // Add missing dependencies
    inactiveColor,
    onPlayerReady,
    tags,
    restProps,
  ]);

  return (
    <>
      {isLoading ? (
        <div className="rrweb-loading" style={{ width, height }}>
          <PulseSpinnerIcon />
        </div>
      ) : error ? (
        <div className="rrweb-error" style={{ width, height }}>
          Error: {error}
        </div>
      ) : (
        <div className={`rrweb-player-container ${containerClassName}`}>
          <div ref={containerRef} className={`rrweb-player ${className}`} />
        </div>
      )}
    </>
  );
};

export default RRWebPlayer;
